import { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { Calendar, Heart } from "lucide-react";

const WeeklyCalendar = () => {
  const { language } = useContext(AuthContext);
  const [weekData, setWeekData] = useState(null);
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeeklyActivity();
  }, []);

  const fetchWeeklyActivity = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/activity/weekly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeekData(response.data.week);
      setStreak(response.data.streak || 0);
    } catch (error) {
      console.error("Error fetching weekly activity:", error);
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const getDayLabel = (dayOfWeek) => {
    const labels = {
      "Mon": language === "ca" ? "Dl" : "Lu",
      "Tue": language === "ca" ? "Dt" : "Ma",
      "Wed": language === "ca" ? "Dc" : "Mi",
      "Thu": language === "ca" ? "Dj" : "Ju",
      "Fri": language === "ca" ? "Dv" : "Vi",
      "Sat": language === "ca" ? "Ds" : "Sá",
      "Sun": language === "ca" ? "Dg" : "Do"
    };
    return labels[dayOfWeek] || dayOfWeek;
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 border border-teal-400/10">
        <div className="animate-pulse flex gap-4 justify-center">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="w-10 h-10 rounded-full bg-slate-700/50" />
          ))}
        </div>
      </div>
    );
  }

  const activeDays = weekData?.filter(d => d.has_activity).length || 0;

  return (
    <div data-testid="weekly-calendar" className="glass rounded-2xl p-6 border border-teal-400/10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-teal-300" />
          <h3 className="text-lg font-semibold text-gray-200">
            {t("Tu semana", "La teva setmana")}
          </h3>
        </div>
        <span className="text-sm text-teal-300">
          {activeDays}/7 {t("días", "dies")}
        </span>
      </div>
      
      <div className="flex justify-between items-end gap-2">
        {weekData?.map((day, index) => (
          <div 
            key={day.date} 
            className="flex flex-col items-center gap-1.5"
            data-testid={`day-${index}`}
          >
            <span className="text-xs text-gray-500">{getDayLabel(day.day_of_week)}</span>
            <div 
              className={`
                w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium
                transition-all duration-300 relative
                ${day.is_today 
                  ? 'ring-2 ring-teal-400 ring-offset-2 ring-offset-slate-900' 
                  : ''}
                ${day.has_activity 
                  ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30' 
                  : 'bg-slate-800/60 text-gray-500 border border-slate-700'}
              `}
            >
              {day.has_activity ? (
                <Heart className="w-4 h-4 fill-current" />
              ) : (
                day.day_number
              )}
            </div>
          </div>
        ))}
      </div>

      {streak > 0 && (
        <div data-testid="streak-counter" className="hidden" aria-hidden="true">
          {streak}
        </div>
      )}
      
      <p className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center gap-1.5">
        <Heart className="w-3.5 h-3.5 text-violet-400 fill-violet-400" />
        <span>
          {activeDays === 7 
            ? t("¡Semana perfecta!", "Setmana perfecta!")
            : streak >= 5 
              ? t("¡Gran racha! Sigue así", "Gran ratxa! Segueix així")
              : activeDays >= 3 
                ? t("¡Muy bien! Sigue así", "Molt bé! Segueix així")
                : t("Cada día cuenta", "Cada dia compta")
          }
        </span>
        <Heart className="w-3.5 h-3.5 text-violet-400 fill-violet-400" />
      </p>
    </div>
  );
};

export default WeeklyCalendar;
